import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AssignerDto,
  CreateModificationStackCommand,
  IModificationStacksClient,
  ModificationStackDto,
  ModificationStacksClient,
  ModificationStackSpecDto,
  PublishModificationStackCommand,
  UpdateModificationStackCommand,
} from "app/ApiClient";
import { IState } from "slices";
import { LoadingState } from "types";
import { initialModificationStacks } from "./modificationStacksState";
import { saveAs } from "file-saver";
import { getStackables } from "slices/stackables/stackablesSlice";

const modificationStacksClient: IModificationStacksClient = new ModificationStacksClient(process.env.REACT_APP_API_URL);

export const createModificationStack = createAsyncThunk(
  "modificationStacks/createModificationStack",
  async (command: CreateModificationStackCommand, { rejectWithValue }) => {
    try {
      const result = await modificationStacksClient.create(command);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const publishModificationStack = createAsyncThunk(
  "modificationStacks/publishModificationStack",
  async (command: PublishModificationStackCommand, { rejectWithValue }) => {
    try {
      const result = await modificationStacksClient.publish(command);     
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateModificationStack = createAsyncThunk(
  "modificationStacks/updateModificationStack",
  async (command: UpdateModificationStackCommand, { rejectWithValue }) => {
    try {
      const result = await modificationStacksClient.update(command);     
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getStacksSpecs = createAsyncThunk(
  "modificationStacks/getStackSpecs",
  async ({ projectId, versionId, stackId}: { projectId: number; versionId: string,stackId: number},{dispatch, rejectWithValue }) => {
    try {
      const result = await modificationStacksClient.getStackSpecs(stackId);
      if (result.assigner != null){      
        await dispatch(getStackables({ projectId, versionId, assigner: result.assigner.name ?? "" }));
      }   
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);
export const getStacksSpecsDownload = createAsyncThunk(
  "modificationStacks/getStackSpecsDownload",
  async (stackId: number, { rejectWithValue }) => {
    try {
      const result = await modificationStacksClient.getStackSpecsDownload(stackId);
      saveAs(result.data, result.fileName);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getModificationStacks = createAsyncThunk(
  "modificationStacks/getByProjectId",
  async ({ projectId, versionId }: { projectId: number; versionId: string }, { rejectWithValue }) => {
    try {
      const result = await modificationStacksClient.getByProjectId(projectId, versionId);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getModificationStackStatus = createAsyncThunk(
  "modificationStacks/getModificationStatus",
  async (token: string, { rejectWithValue }) => {
    try {
      const result = await modificationStacksClient.getStatus(token);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const modificationStacksSlice = createSlice({
  name: "modificationStacks",
  initialState: initialModificationStacks,
  reducers: {
    resetModificationStacks: () => initialModificationStacks,
    setSelectedAssigner: (state, { payload }: PayloadAction<AssignerDto | null>) => {
      state.selectedAssigner = payload;
    },
    setCopiedStacks: (state, { payload }: PayloadAction<ModificationStackSpecDto | null>) => {
      state.copiedSpecs = payload;
    },
    setCreateStackDialogOpened: (state, { payload }: PayloadAction<boolean>) => {
      state.isCreateStackDialogOpened = payload;
    },
    setSelectedModificationStack: (state, { payload }: PayloadAction<ModificationStackDto>) => {
      state.selectedStack = payload;
    },
    setStackSpecsDialogOpened: (state, { payload }: PayloadAction<boolean>) => {
      state.isStackSpecsDialogOpened = payload;
    },
    resetModificationStackStatus: (state) => {
      state.errors = undefined;
      state.currentStatus = null;
      state.getStatusLoading = LoadingState.idle;
      state.publishLoading = LoadingState.idle;
      state.publishResult = null;
      state.createdStack = null;
      state.createLoading = LoadingState.idle;
    },
  },
  extraReducers: (builder) => {
    // getModificationStacks
    builder.addCase(getModificationStacks.pending, (state) => {
      state.loading = LoadingState.pending;
    });
    builder.addCase(getModificationStacks.fulfilled, (state, { payload }) => {
      state.loading = LoadingState.succeed;
      state.modificationStacks = payload;
    });
    builder.addCase(getModificationStacks.rejected, (state) => {
      state.loading = LoadingState.failed;
    });

    // createModificationStack
    builder.addCase(createModificationStack.pending, (state) => {
      state.createLoading = LoadingState.pending;
      state.createdStack = null;
    });
    builder.addCase(createModificationStack.fulfilled, (state, { payload }) => {
      state.createLoading = LoadingState.succeed;
      state.createdStack = payload;
      state.modificationStacks = [payload, ...state.modificationStacks];
    });
    builder.addCase(createModificationStack.rejected, (state, { error: { message } }) => {
      state.createLoading = LoadingState.failed;
      state.errors = message;
    });

    // get stack specs
    builder.addCase(getStacksSpecs.pending, (state) => {
      state.specsLoading = LoadingState.pending;
      state.specs = null;
    });
    builder.addCase(getStacksSpecs.fulfilled, (state, { payload }) => {
      state.specsLoading = LoadingState.succeed;
      state.specs = payload;
    });
    builder.addCase(getStacksSpecs.rejected, (state, { error: { message } }) => {
      state.specsLoading = LoadingState.failed;
      state.errors = message;
    });

     // get stack specs downloads
     builder.addCase(getStacksSpecsDownload.pending, (state) => {
      state.downloadingState = LoadingState.pending;
      state.errors = undefined;
    });
    builder.addCase(getStacksSpecsDownload.fulfilled, (state) => {
      state.downloadingState = LoadingState.succeed;
      state.errors = undefined;
    });
    builder.addCase(getStacksSpecsDownload.rejected, (state, { error: { message } }) => {
      state.downloadingState = LoadingState.failed;      
      state.errors = message;
    });

    // publishModificationStack
    builder.addCase(publishModificationStack.pending, (state) => {
      state.publishLoading = LoadingState.pending;
      state.getStatusLoading = LoadingState.idle;
      state.currentStatus = null;
    });
    builder.addCase(publishModificationStack.fulfilled, (state, { payload }) => {
      state.publishLoading = LoadingState.succeed;
      state.publishResult = payload;
    });
    builder.addCase(publishModificationStack.rejected, (state, { error: { message } }) => {
      state.publishLoading = LoadingState.failed;
      state.errors = message;
    });

    // updateModificationStack
    builder.addCase(updateModificationStack.pending, (state) => {
      state.updateLoading = LoadingState.pending;
    });
    builder.addCase(updateModificationStack.fulfilled, (state, { payload }) => {
      state.updateLoading = LoadingState.succeed;
      state.modificationStacks = state.modificationStacks.map((item) => (item.id === payload.id ? payload : item));
    });
    builder.addCase(updateModificationStack.rejected, (state) => {
      state.updateLoading = LoadingState.failed;
    });

    // getModificationStackStatus
    builder.addCase(getModificationStackStatus.pending, (state) => {
      state.getStatusLoading = LoadingState.pending;
    });
    builder.addCase(getModificationStackStatus.fulfilled, (state, { payload }) => {
      state.getStatusLoading = LoadingState.succeed;
      state.currentStatus = payload;
    });
    builder.addCase(getModificationStackStatus.rejected, (state, { error: { message } }) => {
      state.getStatusLoading = LoadingState.failed;
      state.errors = message;
    });
  },
});

export const {
  resetModificationStacks,
  resetModificationStackStatus,
  setCreateStackDialogOpened,
  setSelectedModificationStack,
  setStackSpecsDialogOpened,
  setCopiedStacks,
  setSelectedAssigner,
} = modificationStacksSlice.actions;
export const modificationStacksSelector = (state: IState) => state.modificationStacks;
export default modificationStacksSlice.reducer;
